import React, { useCallback, useEffect, useMemo, useState } from "react";
import { App, Button, Card, Form, Input, InputNumber, Modal, Space, Table, Tag, Typography } from "antd";
import { useAuth } from "../../../context/AuthContext";
import { approveBudgetRequest, listPendingBudgetRequests, rejectBudgetRequest, listUsers } from "../../../services/api";
import PageHeader from "../../../components/PageHeader";
import { pageShellStyle } from "../../../theme";
import { formatUsd } from "../utils";

const { Text } = Typography;

export default function AdminBudgetRequestsPage() {
  const { token, isAdmin } = useAuth();
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [userDirectory, setUserDirectory] = useState({});
  const [allUsers, setAllUsers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [decisionType, setDecisionType] = useState("approve");
  const [activeRow, setActiveRow] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    if (!token || !isAdmin) return;
    setLoading(true);
    try {
      const [res, usersRes] = await Promise.all([listPendingBudgetRequests(token, 200), listUsers(token)]);
      setRows(Array.isArray(res?.data?.requests) ? res.data.requests : []);
      setUserDirectory(res?.data?.user_directory && typeof res.data.user_directory === "object" ? res.data.user_directory : {});
      setAllUsers(Array.isArray(usersRes?.data) ? usersRes.data : []);
    } catch (e) {
      message.error(e?.response?.data?.detail || "Failed to load pending requests");
    } finally {
      setLoading(false);
    }
  }, [token, isAdmin, message]);

  useEffect(() => {
    load();
  }, [load]);

  const openDecision = (row, type) => {
    setActiveRow(row);
    setDecisionType(type);
    form.setFieldsValue({
      approved_amount_usd: type === "approve" ? Number(row?.requested_amount_usd || 0) : 0,
      notes: "",
    });
    setModalOpen(true);
  };

  const submitDecision = async () => {
    const v = await form.validateFields();
    if (!token || !activeRow?.request_id) return;
    setSubmitting(true);
    try {
      if (decisionType === "approve") {
        await approveBudgetRequest(token, activeRow.request_id, {
          approved_amount_usd: Number(v.approved_amount_usd || 0),
          notes: v.notes || null,
        });
        message.success("Request approved");
      } else {
        await rejectBudgetRequest(token, activeRow.request_id, { notes: v.notes || null });
        message.success("Request rejected");
      }
      setModalOpen(false);
      load();
    } catch (e) {
      message.error(e?.response?.data?.detail || "Failed to update request");
    } finally {
      setSubmitting(false);
    }
  };

  const usersById = useMemo(() => {
    const map = {};
    for (const u of allUsers || []) {
      const id = String(u?._id || "");
      if (!id) continue;
      map[id] = String(u?.username || "");
    }
    return map;
  }, [allUsers]);

  const columns = useMemo(
    () => [
      { title: "Created", dataIndex: "created_at", key: "created_at", width: 170, render: (v) => String(v || "—").replace("T", " ").slice(0, 19) },
      { title: "Project", dataIndex: "project_id", key: "project_id", width: 200, ellipsis: true },
      {
        title: "User",
        dataIndex: "user_id",
        key: "user_id",
        width: 220,
        ellipsis: true,
        render: (uid, row) => {
          const id = String(uid || "");
          const name = row?.username || usersById?.[id] || userDirectory?.[id] || "";
          return name || id;
        },
      },
      { title: "Requested", dataIndex: "requested_amount_usd", key: "requested_amount_usd", width: 120, render: (v) => formatUsd(v) },
      { title: "Reason", dataIndex: "reason", key: "reason", ellipsis: true },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 110,
        render: (v) => <Tag color="blue">{String(v || "pending")}</Tag>,
      },
      {
        title: "Action",
        key: "action",
        width: 170,
        render: (_, row) => (
          <Space>
            <Button size="small" type="primary" onClick={() => openDecision(row, "approve")}>
              Approve
            </Button>
            <Button size="small" danger onClick={() => openDecision(row, "reject")}>
              Reject
            </Button>
          </Space>
        ),
      },
    ],
    [userDirectory, usersById],
  );

  if (!isAdmin) {
    return (
      <div style={{ ...pageShellStyle, padding: 18 }}>
        <Card>
          <Text type="secondary">Admin access required.</Text>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ ...pageShellStyle, padding: 18 }}>
      <PageHeader title="Budget requests" subtitle="Approve or reject user budget extension requests" />
      <Card loading={loading}>
        <Table rowKey={(r) => r.request_id} columns={columns} dataSource={rows} pagination={{ pageSize: 10 }} />
      </Card>

      <Modal
        title={decisionType === "approve" ? "Approve request" : "Reject request"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={submitDecision}
        confirmLoading={submitting}
        okText={decisionType === "approve" ? "Approve" : "Reject"}
        okButtonProps={decisionType === "reject" ? { danger: true } : undefined}
      >
        {activeRow ? (
          <Space direction="vertical" style={{ width: "100%" }} size={10}>
            <Text type="secondary">
              Project: {activeRow.project_id} | User: {activeRow.user_id}
            </Text>
            <Text>
              Requested: <b>{formatUsd(activeRow.requested_amount_usd)}</b>
            </Text>
            <Form layout="vertical" form={form}>
              {decisionType === "approve" ? (
                <Form.Item
                  label="Approved additional budget (USD)"
                  name="approved_amount_usd"
                  rules={[{ required: true, message: "Enter approved amount" }]}
                >
                  <InputNumber min={0} precision={2} style={{ width: "100%" }} />
                </Form.Item>
              ) : null}
              <Form.Item label="Notes" name="notes">
                <Input.TextArea rows={4} placeholder="Optional notes" />
              </Form.Item>
            </Form>
          </Space>
        ) : null}
      </Modal>
    </div>
  );
}

